import { round, sortAlphaBeta } from '.';
import {
  MIXTURE_STATE,
  PRODUCT_STATE,
  USAGE_ISSUES,
  USAGE_STATE,
  UsageIssues,
  UsageState,
} from '../constants/phytosConstants';
import { I18n } from '../i18n';
import { ClusterId } from '../slices/clusters';
import { ParcelId } from '../slices/parcels';
import {
  CustomProduct,
  Mixture,
  MixtureProductUsage,
  PhytoRegister,
  Product,
  ProductId,
  ProductUsage,
  ProductUsageId,
} from '../slices/phyto';
import { TaskId } from '../slices/tasks';

const t = I18n.t;

export const formatProductFunctions = (product: Product) => {
  let functions = '';
  product.functions?.map((f, index) => {
    return (functions += `${f}${index < product.functions.length - 1 ? ', ' : ''}`);
  });
  return functions;
};

export const formatMixtureFunction = (mixture: Mixture) => {
  if (!mixture.product_usages) {
    return '';
  }
  if (mixture.product_usages.length === 1 && !mixture.product_usages.at(0)!.product_state) {
    return t('Admin.unlisted_function');
  }
  const functions = mixture.product_usages.map((pu) => pu.function).filter((f) => !!f);
  const combinedArray = [...new Set(functions.flat(1))];
  const functionLabel = combinedArray.length === 1 ? combinedArray[0]! : t('Admin.multiTreatment');
  return functionLabel;
};

export const formatReentryDelays = (delays?: string[]) => {
  return delays?.join(', ') || '-';
};

const checkUsageAuthorization = (usage: UsageState) => {
  return (
    usage.toLowerCase() === USAGE_STATE.AUTHORIZED.toLowerCase() ||
    usage.toLowerCase() === USAGE_STATE.TEMPORARILY.toLowerCase()
  );
};

export const isUsageAuthorized = (usage?: ProductUsage | UsageState) => {
  return usage && typeof usage === 'string'
    ? checkUsageAuthorization(usage)
    : typeof usage?.state === 'string'
      ? checkUsageAuthorization(usage.state)
      : false;
};

export const isMixtureCompliant = (mixture: Mixture) => {
  return mixture?.status === MIXTURE_STATE.COMPLIANT;
};

export const isProductMixAuthorized = (productUsages?: MixtureProductUsage[]) => {
  return !(productUsages || []).reduce(
    (p, u) => p || ('unauthorized_with_other_product_ids' in u && u.unauthorized_with_other_product_ids.length > 0),
    false,
  );
};

export const isCustomProduct = (product: Product): product is CustomProduct => {
  return !!product?.cluster_id;
};

export function getUsageProblems(usage?: MixtureProductUsage) {
  if (!usage) return [];

  const problems: UsageIssues[] = [];
  if (usage.product_state === PRODUCT_STATE.WITHDRAW) {
    problems.push(USAGE_ISSUES.PRODUCT_WITHDRAWN);
  }
  if (!isUsageAuthorized(usage.product_usage_state)) {
    problems.push(USAGE_ISSUES.USAGE_WITHDRAWN);
  }
  if (
    // a custom product with no max dose is always good
    // a normal product with no max dose is always in overdose
    (!!usage.product_state || usage.product_usage_dose > 0) &&
    round(usage.dose) > round(usage.product_usage_dose)
  ) {
    problems.push(USAGE_ISSUES.OVERDOSE);
  }

  return problems;
}

export function getUsageProblemsLabel(problems: UsageIssues[]) {
  const labels = [];
  for (let i = 0; i < problems.length; i++) {
    const problem = problems[i]!;

    const label = t(`CreateTaskModal.mixture_input_error_${problem}`);
    labels.push(i === 0 ? label : label.toLowerCase());
  }
  return labels.join(', ');
}

export function getProductLabelForList(product: Product) {
  const formattedFunctions = formatProductFunctions(product);
  return (
    product?.name &&
    `${product.name.toLocaleUpperCase()}${
      isCustomProduct(product)
        ? ` (${t('Admin.unlisted_product')})`
        : formattedFunctions
          ? ` (${formattedFunctions})`
          : ''
    }`
  );
}

function getPhytoReportsGroupedByCluster(phytoRegister: PhytoRegister[]) {
  const byCluster: Record<ClusterId, { cluster_id: ClusterId; cluster_name: string; reports: PhytoRegister[] }> = {};
  for (const report of phytoRegister) {
    const id = report.task_cluster_id;

    byCluster[id] ??= {
      cluster_id: id,
      cluster_name: report.task_cluster_name,
      reports: [],
    };

    byCluster[id].reports.push(report);
  }

  return byCluster;
}

type RegisterByParcel = {
  reports: (PhytoRegister & { id: `${ParcelId}/${ProductUsageId}/${string}` })[];
  parcel_id: ParcelId;
  parcel_name: string;
  parcel_area: number;
  products: { product_id: ProductId; product_name: string; product_usage_ids: Set<ProductUsageId> }[];
  total_quantity: number;
  dose_unit: string;
  total_worked_area: number;
  count_parcel_passage: number;
  task_ids: TaskId[];
  dates: string[];
};

export function getPhytoReportsGroupedByClusterAndParcel(phytoRegisters: PhytoRegister[]) {
  const phytoRegistersByCluster = getPhytoReportsGroupedByCluster(phytoRegisters);

  const byClusterAndParcel: Record<
    ClusterId,
    { cluster_id: ClusterId; cluster_name: string; reports: RegisterByParcel[] }
  > = {};

  for (const stringClusterId in phytoRegistersByCluster) {
    const clusterId = Number(stringClusterId);
    const ungroupedReports = phytoRegistersByCluster[clusterId]?.reports || [];
    const groupedReports: Record<ParcelId, RegisterByParcel> = {};

    for (const report of ungroupedReports) {
      const id = report.parcel_id;

      groupedReports[id] ??= {
        reports: [],
        parcel_id: id,
        parcel_name: report.parcel_name,
        parcel_area: report.parcel_area,
        products: [],
        total_quantity: 0,
        dose_unit: report.dose_unit,
        total_worked_area: 0,
        count_parcel_passage: 0,
        task_ids: [],
        dates: [],
      };

      groupedReports[id].reports.push({
        ...report,
        id: `${report.parcel_id}/${report.product_usage_id}/${report.date}`,
      });
      // one product per product_id
      groupedReports[id].products.find((p) => p.product_id === report.product_id)
        ? null
        : groupedReports[id].products.push({
            product_id: report.product_id,
            product_name: report.product_name,
            product_usage_ids: new Set(),
          });
      // group product usages by product id
      (groupedReports[id]?.products || [])
        .find((p) => p.product_id === report.product_id)
        ?.product_usage_ids.add(report.product_usage_id);
      groupedReports[id].total_quantity += report.total_quantity;

      const areTasksAlreadyCounted = report.task_ids.every((task_id) => groupedReports[id]?.task_ids.includes(task_id));

      groupedReports[id].total_worked_area += areTasksAlreadyCounted ? 0 : report.total_worked_area;
      groupedReports[id].count_parcel_passage += areTasksAlreadyCounted ? 0 : report.count_parcel_passage;
      groupedReports[id].task_ids.push(...report.task_ids);
      groupedReports[id].dates.push(report.date);
    }

    const sortedGroupedReports = Object.values(groupedReports).map((groupedReport) => ({
      ...groupedReport,
      dates: groupedReport.dates.sort(),
    }));

    // sort by the latest date of each parcel
    byClusterAndParcel[clusterId] = {
      cluster_id: clusterId,
      cluster_name: phytoRegistersByCluster[clusterId]?.cluster_name || '',
      reports: sortedGroupedReports.sort((a, b) => sortAlphaBeta(b.dates.at(-1), a.dates.at(-1))),
    };
  }

  return byClusterAndParcel;
}
